/**
 * Evaluates funnel step for the current page and manages dismiss / decline state.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { FunnelPageType } from '@template-project/funnels-contract';
import { useFunnelContext } from './funnel-provider';
import type { StepExperience, FunnelOffer, PageContext } from './types';
import { funnelLogger } from './logger';
import { addDeclinedStep, getDeclinedSteps } from './declined-steps';
import { buildCookieString } from './cookie-utils';
import { currentStepStorageKey } from './runtime-storage';
export type { FunnelPageType } from '@template-project/funnels-contract';

try {
  sessionStorage.removeItem('funnel_dismissed_steps');
} catch {
  /* ignore */
}

export interface UseFunnelExperienceResult {
  loading: boolean;
  experience: StepExperience | null;
  offer: FunnelOffer | null;
  experienceType: string | null;
  activeFunnelId: string | null;
  dismiss: () => void;
  decline: () => void;
  overrideExperience: (step: StepExperience) => void;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string | null): void {
  if (typeof document === 'undefined') return;
  document.cookie = buildCookieString(name, value, window.location.hostname);
}

function stableListKey(values: readonly string[] | undefined): string {
  if (!values || values.length === 0) return '';
  return values.join('\u0001');
}

function stableRecordKey(values: Record<string, string> | undefined): string {
  if (!values) return '';
  const keys = Object.keys(values).sort();
  if (keys.length === 0) return '';
  return keys.map((key) => `${key}\u0001${values[key] ?? ''}`).join('\u0002');
}

function buildEvaluationContextKey(
  pageType: FunnelPageType,
  context: Omit<PageContext, 'pageType'>,
  derivedKeys: {
    cartProductIdsKey: string;
    cartCollectionsKey: string;
    productCollectionsKey: string;
    productTagsKey: string;
    orderProductIdsKey: string;
    urlParametersKey: string;
  },
): string {
  return JSON.stringify({
    pageType,
    url: context.url ?? '',
    productId: context.productId ?? '',
    collectionId: context.collectionId ?? '',
    cartToken: context.cartToken ?? '',
    cartTotal: context.cartTotal ?? null,
    cartItemCount: context.cartItemCount ?? null,
    cartProductIdsKey: derivedKeys.cartProductIdsKey,
    cartCollectionsKey: derivedKeys.cartCollectionsKey,
    country: context.country ?? '',
    isReturning: context.isReturning ?? null,
    pageViewCount: context.pageViewCount ?? null,
    productCollectionsKey: derivedKeys.productCollectionsKey,
    productTagsKey: derivedKeys.productTagsKey,
    orderId: context.orderId ?? '',
    orderProductIdsKey: derivedKeys.orderProductIdsKey,
    orderTotal: context.orderTotal ?? null,
    urlParametersKey: derivedKeys.urlParametersKey,
  });
}

function canRetryWithFreshAssignment(pageType: FunnelPageType, isLocked: boolean): boolean {
  if (!isLocked) return false;
  return pageType !== 'cart' && pageType !== 'checkout' && pageType !== 'post_purchase';
}

function getStoredCurrentStepId(funnelId: string | null): string | null {
  if (!funnelId) return null;
  try {
    const key = currentStepStorageKey(funnelId);
    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue) return sessionValue;

    const cookieValue = readCookie(key);
    if (cookieValue) {
      sessionStorage.setItem(key, cookieValue);
      return cookieValue;
    }

    return null;
  } catch {
    return readCookie(currentStepStorageKey(funnelId));
  }
}

function setStoredCurrentStepId(funnelId: string | null, stepId: string | null): void {
  if (!funnelId) return;
  try {
    const key = currentStepStorageKey(funnelId);
    if (stepId) {
      sessionStorage.setItem(key, stepId);
    } else {
      sessionStorage.removeItem(key);
    }
    writeCookie(key, stepId);
  } catch {
    writeCookie(currentStepStorageKey(funnelId), stepId);
  }
}

export function useFunnelExperience(
  pageType: FunnelPageType,
  context: Omit<PageContext, 'pageType'>,
): UseFunnelExperienceResult {
  const {
    loading: providerLoading,
    assignPageFunnel,
    evaluatePageStep,
    assignedFunnelId,
    assignedFunnelName,
    isAssignedFunnelLocked,
    lockAssignedFunnel,
    clearAssignedFunnel,
  } = useFunnelContext();
  const [loading, setLoading] = useState(true);
  const [experience, setExperience] = useState<StepExperience | null>(null);
  const [activeFunnelId, setActiveFunnelId] = useState<string | null>(assignedFunnelId);
  const [hidden, setHidden] = useState(false);
  const stepIdRef = useRef<string | null>(null);
  const assignedFunnelIdRef = useRef<string | null>(assignedFunnelId);
  assignedFunnelIdRef.current = assignedFunnelId;
  const assignedFunnelNameRef = useRef<string | null>(assignedFunnelName);
  assignedFunnelNameRef.current = assignedFunnelName;
  const isAssignedFunnelLockedRef = useRef(isAssignedFunnelLocked);
  isAssignedFunnelLockedRef.current = isAssignedFunnelLocked;

  const evaluateRef = useRef(evaluatePageStep);
  evaluateRef.current = evaluatePageStep;

  const assignRef = useRef(assignPageFunnel);
  assignRef.current = assignPageFunnel;

  const lockAssignedRef = useRef(lockAssignedFunnel);
  lockAssignedRef.current = lockAssignedFunnel;

  const clearAssignedRef = useRef(clearAssignedFunnel);
  clearAssignedRef.current = clearAssignedFunnel;

  const cartProductIdsKey = useMemo(() => stableListKey(context.cartProductIds), [context.cartProductIds]);
  const cartCollectionsKey = useMemo(() => stableListKey(context.cartCollections), [context.cartCollections]);
  const productCollectionsKey = useMemo(() => stableListKey(context.productCollections), [context.productCollections]);
  const productTagsKey = useMemo(() => stableListKey(context.productTags), [context.productTags]);
  const orderProductIdsKey = useMemo(() => stableListKey(context.orderProductIds), [context.orderProductIds]);
  const urlParametersKey = useMemo(() => stableRecordKey(context.urlParameters), [context.urlParameters]);
  const evaluationContextKey = useMemo(
    () => buildEvaluationContextKey(pageType, context, {
      cartProductIdsKey,
      cartCollectionsKey,
      productCollectionsKey,
      productTagsKey,
      orderProductIdsKey,
      urlParametersKey,
    }),
    [
      pageType,
      context.url,
      context.productId,
      context.collectionId,
      context.cartToken,
      context.cartTotal,
      context.cartItemCount,
      cartProductIdsKey,
      cartCollectionsKey,
      context.country,
      context.isReturning,
      context.pageViewCount,
      productCollectionsKey,
      productTagsKey,
      context.orderId,
      orderProductIdsKey,
      context.orderTotal,
      urlParametersKey,
    ],
  );
  const exhaustedContextKeyRef = useRef<string | null>(null);

  useEffect(() => {
    stepIdRef.current = getStoredCurrentStepId(assignedFunnelId);
  }, [assignedFunnelId]);

  useEffect(() => {
    if (!assignedFunnelId && exhaustedContextKeyRef.current === evaluationContextKey) {
      setExperience(null);
      setLoading(false);
      return;
    }

    setHidden(false);

    // assignPageFunnel toggles FunnelProvider `loading` (providerLoading). That must NOT be in this
    // effect's dependency array: re-running the effect while assign is in flight runs cleanup and
    // sets `cancelled`, aborting evaluate mid-assign and causing an assign/evaluate loop (network storm).

    setLoading(true);
    let cancelled = false;

    async function evaluate() {
      try {
        let activeFunnelId = assignedFunnelIdRef.current;
        let activeFunnelName = assignedFunnelNameRef.current;
        let isLocked = isAssignedFunnelLockedRef.current;
        let shouldRetryFreshAssignment = Boolean(activeFunnelId);

        const cartTokenMissing = !context.cartToken;
        if (isLocked && cartTokenMissing && (pageType === 'cart' || pageType === 'checkout')) {
          funnelLogger.warn('Funnel continuity was reset because cart context is missing on a locked journey', {
            pageType,
            funnelId: activeFunnelId,
            reason: 'missing_cart_token_for_locked_journey',
            url: context.url ?? null,
          });
          clearAssignedRef.current();
          activeFunnelId = null;
          activeFunnelName = null;
          isLocked = false;
          stepIdRef.current = null;
        }

        if (!activeFunnelId && exhaustedContextKeyRef.current === evaluationContextKey) {
          setExperience(null);
          setLoading(false);
          return;
        }

        for (let attempt = 0; attempt < 2; attempt += 1) {
          if (!activeFunnelId) {
            const assignment = await assignRef.current({
              ...context,
              pageType,
            });
            if (cancelled) return;
            activeFunnelId = assignment.funnelId || null;
            activeFunnelName = assignment.funnelName || null;
            isLocked = assignment.cached;
            shouldRetryFreshAssignment = false;
            stepIdRef.current = getStoredCurrentStepId(activeFunnelId);
          }

          if (!activeFunnelId) {
            funnelLogger.warn('No funnel is available for this page after assignment', {
              pageType,
              reason: 'assign_returned_empty',
              url: context.url ?? null,
            });
            setExperience(null);
            setActiveFunnelId(null);
            setLoading(false);
            return;
          }

          const result = await evaluateRef.current({
            funnelId: activeFunnelId,
            currentStepId: stepIdRef.current ?? undefined,
            event: 'page_view',
            pageContext: {
              ...context,
              pageType,
            },
          });

          if (cancelled) return;

          funnelLogger.info('Funnel step evaluated', {
            pageType,
            hasExperience: !!result.step,
            ...(result.step?.name ? { stepName: result.step.name } : {}),
            hasOffer: !!result.step?.offer,
          });

          if (result.hasExperience && result.step) {
            exhaustedContextKeyRef.current = null;
            if (getDeclinedSteps().has(result.step.id)) {
              setHidden(true);
              setExperience(null);
            } else {
              stepIdRef.current = result.step.id;
              setStoredCurrentStepId(activeFunnelId, result.step.id);
              setExperience(result.step);
              setActiveFunnelId(activeFunnelId);
              if (!isLocked) {
                lockAssignedRef.current(activeFunnelId, activeFunnelName);
              }
            }
            return;
          }

          const previousFunnelId = activeFunnelId;
          const previousStepId = stepIdRef.current;
          stepIdRef.current = null;
          setStoredCurrentStepId(previousFunnelId, null);
          setExperience(null);
          setActiveFunnelId(null);

          funnelLogger.warn('Assigned funnel has no eligible step for this page context', {
            pageType,
            funnelId: previousFunnelId,
            funnelName: activeFunnelName ?? null,
            currentStepId: previousStepId ?? null,
            isLocked,
            willRetryFreshAssignment:
              attempt === 0 && shouldRetryFreshAssignment && canRetryWithFreshAssignment(pageType, isLocked),
            url: context.url ?? null,
            cartToken: context.cartToken ?? null,
            orderId: context.orderId ?? null,
            reason: isLocked ? 'locked_funnel_no_matching_step' : 'assigned_funnel_no_matching_step',
          });

          if (attempt === 0 && shouldRetryFreshAssignment && canRetryWithFreshAssignment(pageType, isLocked)) {
            clearAssignedRef.current();
            activeFunnelId = null;
            activeFunnelName = null;
            isLocked = false;
            shouldRetryFreshAssignment = false;
            continue;
          }

          if (!isLocked) {
            exhaustedContextKeyRef.current = evaluationContextKey;
            clearAssignedRef.current();
          }
          return;
        }
      } catch (err) {
        if (cancelled) return;
        stepIdRef.current = null;
        setStoredCurrentStepId(assignedFunnelId, null);
        setExperience(null);
        setActiveFunnelId(null);
        setHidden(false);
        if (!isAssignedFunnelLockedRef.current) {
          clearAssignedRef.current();
        }
        funnelLogger.error('Funnel evaluation failed; suppressing experience for this page', err instanceof Error ? err : undefined, {
          pageType,
          funnelId: assignedFunnelIdRef.current ?? null,
          url: context.url ?? null,
          reason: 'evaluation_failed',
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void evaluate();
    return () => {
      cancelled = true;
    };
  }, [
    // assignedFunnelId intentionally omitted: it flips during assign/clear inside this effect.
    // Listing it re-runs the effect, cleanup sets cancelled, in-flight evaluate aborts → assign/eval storm.
    // assignedFunnelIdRef is synced every render; step id still tracks assignedFunnelId via effect above.
    pageType,
    context.url,
    context.productId,
    context.collectionId,
    context.cartToken,
    context.cartTotal,
    context.cartItemCount,
    cartProductIdsKey,
    cartCollectionsKey,
    context.country,
    context.isReturning,
    context.pageViewCount,
    productCollectionsKey,
    productTagsKey,
    context.orderId,
    orderProductIdsKey,
    context.orderTotal,
    evaluationContextKey,
  ]);

  const dismiss = useCallback(() => {
    setHidden(true);
  }, []);

  const decline = useCallback(() => {
    if (stepIdRef.current) {
      addDeclinedStep(stepIdRef.current);
    }
    stepIdRef.current = null;
    setStoredCurrentStepId(activeFunnelId, null);
    // TF-AUDIT-004: Clear experience to prevent stale state after decline.
    setExperience(null);
    setActiveFunnelId(null);
    setHidden(true);
  }, [activeFunnelId]);

  const overrideExperience = useCallback((step: StepExperience) => {
    stepIdRef.current = step.id;
    setStoredCurrentStepId(activeFunnelId, step.id);
    setExperience(step);
    setHidden(false);
  }, [activeFunnelId]);

  const isVisible = !loading && !hidden && experience !== null;

  return {
    loading: loading || providerLoading,
    experience: isVisible ? experience : null,
    offer: isVisible ? (experience?.offer ?? null) : null,
    experienceType: isVisible ? (experience?.experienceType ?? null) : null,
    activeFunnelId: isVisible ? activeFunnelId : null,
    dismiss,
    decline,
    overrideExperience,
  };
}
